(() => {
  if (customElements.get('fsk-skin-advisor')) return;
  class SkinAdvisor extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.copy = JSON.parse(this.querySelector('[data-copy]').textContent);
      this.treatments = JSON.parse(this.querySelector('[data-treatments]').textContent);
      this.state = { skin: '', goals: [], preferences: [], routine: '', budget: null };
      this.groups = ['skin', 'goals', 'preferences', 'routine', 'budget'];
      this.index = 0;
      this.items = [];
      this.excluded = [];
      this.root = (this.dataset.root || '/').replace(/\/?$/, '/');
      this.money = new Intl.NumberFormat(document.documentElement.lang || 'de-DE', { style: 'currency', currency: 'EUR' });
      this.requestVersion = 0;
      this.querySelector('[data-start]').addEventListener('click', () => this.start());
      this.querySelector('[data-form]').addEventListener('submit', event => { event.preventDefault(); this.next(); });
      this.querySelector('[data-back]').addEventListener('click', () => { this.index = Math.max(0, this.index - 1); this.showStep(); });
      this.querySelectorAll('[data-restart]').forEach(button => button.addEventListener('click', () => this.start(true)));
      this.querySelectorAll('[data-edit]').forEach(button => button.addEventListener('click', () => { this.requestVersion++; this.setPane('experience'); this.index = 0; this.showStep(); }));
      this.querySelector('[data-retry]').addEventListener('click', () => this.recommend());
      this.querySelector('[data-add-routine]').addEventListener('click', () => this.addRoutine());
      this.querySelectorAll('[data-field]').forEach(input => input.addEventListener('change', () => this.choose(input)));
      if (matchMedia('(max-width: 749px)').matches) this.start(false, true);
    }
    setPane(pane) {
      for (const name of ['intro', 'experience', 'loading', 'result', 'error']) this.querySelector(`[data-${name}]`).hidden = name !== pane;
    }
    start(clear = false, quiet = false) {
      this.requestVersion++;
      if (clear) {
        this.state = { skin: '', goals: [], preferences: [], routine: '', budget: null };
        this.querySelector('[data-form]').reset();
        this.excluded = [];
      }
      this.index = 0;
      this.setPane('experience');
      this.showStep(quiet);
      this.sessionPromise = this.session().catch(() => null);
    }
    async session() {
      const url = this.endpoint();
      url.searchParams.set('action', 'session');
      const response = await fetch(url, { mode: 'cors', credentials: 'omit', signal: AbortSignal.timeout(12000) });
      const data = await response.json();
      if (!response.ok || !data.ok || typeof data.token !== 'string') throw new Error(data.code || 'service_unavailable');
      this.token = data.token;
      this.tokenAt = Date.now();
      return data.token;
    }
    endpoint() {
      if (!this.dataset.endpoint) throw new Error('setup_required');
      const url = new URL(this.dataset.endpoint);
      if (url.protocol !== 'https:' && !(['localhost', '127.0.0.1'].includes(url.hostname) && location.hostname === url.hostname)) throw new Error('setup_required');
      return url;
    }
    showStep(quiet = false) {
      this.querySelectorAll('[data-step]').forEach((step, index) => { step.hidden = index !== this.index; });
      this.querySelector('[data-count]').textContent = this.copy.count.replace('__CURRENT__', String(this.index + 1));
      this.querySelector('[data-progress]').style.width = `${(this.index + 1) * 20}%`;
      this.querySelector('[role=progressbar]').setAttribute('aria-valuenow', String(this.index + 1));
      this.querySelector('[data-back]').disabled = this.index === 0;
      this.querySelector('[data-next]').firstChild.textContent = this.index === 4 ? this.copy.finish : this.copy.next;
      this.querySelector('[data-message]').textContent = '';
      this.updateProfile();
      this.updateGoalPriority();
      const target = this.querySelector(`[data-step="${this.groups[this.index]}"] legend`);
      if (!quiet) requestAnimationFrame(() => { target.focus({ preventScroll: true }); this.keepQuestionVisible(target); });
    }
    keepQuestionVisible(question) {
      const form = this.querySelector('[data-form]');
      const experience = this.querySelector('[data-experience]');
      // Keep an already visible question in place. Only reveal a hidden question,
      // without scrolling back to the advisor heading or animating the page.
      const inset = parseFloat(getComputedStyle(experience).scrollMarginTop) || 110;
      const top = form.getBoundingClientRect().top + (parseFloat(getComputedStyle(form).paddingTop) || 0);
      if (top < inset || top + question.offsetHeight > innerHeight - 24) {
        window.scrollTo({ top: Math.max(0, window.scrollY + top - inset), behavior: 'instant' });
      }
    }
    choose(input) {
      this.excluded = [];
      const group = input.dataset.field;
      this.querySelector('[data-message]').textContent = '';
      if (input.type === 'checkbox') {
        let values = this.state[group].filter(value => value !== input.value);
        if (input.checked) {
          if (['none', 'unsure'].includes(input.value)) values = [];
          else values = values.filter(value => !['none', 'unsure'].includes(value));
          if (input.value === 'beginner') values = values.filter(value => value !== 'experienced');
          if (input.value === 'experienced') values = values.filter(value => value !== 'beginner');
          if (values.length >= 3) {
            input.checked = false;
            this.querySelector('[data-message]').textContent = this.copy.max_choices;
          } else values.push(input.value);
        }
        this.state[group] = values;
        this.querySelectorAll(`[data-field="${group}"]`).forEach(choice => { choice.checked = values.includes(choice.value); });
      } else this.state[group] = group === 'budget' ? Number(input.value) : input.value;
      this.updateProfile();
      this.updateGoalPriority();
    }
    updateGoalPriority() {
      this.querySelectorAll('[data-field="goals"]').forEach(input => {
        const marker = input.parentElement.querySelector('i');
        const index = this.state.goals.indexOf(input.value);
        const ranked = index >= 0 && input.value !== 'unsure';
        marker.classList.toggle('fska__priority', ranked);
        marker.textContent = ranked ? String(index + 1) : '';
      });
    }
    labels() {
      const labels = [];
      for (const group of this.groups) {
        const values = Array.isArray(this.state[group]) ? this.state[group] : [this.state[group]];
        for (const value of values) {
          if (value === '' || value === null || ['none', 'unsure'].includes(value)) continue;
          const input = [...this.querySelectorAll(`[data-field="${group}"]`)].find(item => item.value === String(value));
          if (input) labels.push(input.dataset.profileLabel || input.parentElement.querySelector('.fska__option-title').textContent);
        }
      }
      return labels;
    }
    updateProfile() {
      const target = this.querySelector('[data-profile]');
      const labels = this.labels();
      if (!labels.length) labels.push(this.copy.profile_empty);
      target.replaceChildren(...labels.map(label => { const span = document.createElement('span'); span.textContent = label; return span; }));
    }
    next() {
      const value = this.state[this.groups[this.index]];
      if (value === '' || value === null || (Array.isArray(value) && !value.length)) {
        this.querySelector('[data-message]').textContent = Array.isArray(value) ? this.copy.select_many : this.copy.select_one;
        this.querySelector(`[data-step="${this.groups[this.index]}"] input`).focus();
        return;
      }
      if (this.index < 4) { this.index++; this.showStep(); } else this.recommend();
    }
    async recommend() {
      const version = ++this.requestVersion;
      this.setPane('loading');
      try {
        const endpoint = this.endpoint();
        await this.sessionPromise;
        if (!this.token || Date.now() - this.tokenAt > 3500000) await this.session();
        const wait = 2100 - (Date.now() - this.tokenAt);
        if (wait > 0) await new Promise(resolve => setTimeout(resolve, wait));
        const response = await fetch(endpoint, {
          method: 'POST', mode: 'cors', credentials: 'omit', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ profile: this.state, token: this.token, website: this.querySelector('[name=website]').value, exclude: this.excluded }),
          signal: AbortSignal.timeout(65000)
        });
        const result = await response.json();
        if (version !== this.requestVersion) return;
        if (!response.ok || !result.ok || result.mode !== 'ai' || !Array.isArray(result.items) || !result.items.length || result.items.length > 3) throw new Error(result.code || 'recommendation_unavailable');
        const handles = result.items.map(item => item.handle);
        if (handles.some(handle => !/^[a-z0-9][a-z0-9-]{0,179}$/.test(handle)) || new Set(handles).size !== handles.length) throw new Error('recommendation_unavailable');
        const hydrated = await Promise.all(result.items.map(item => this.hydrate(item)));
        if (version !== this.requestVersion) return;
        if (hydrated.some(item => !item)) {
          const next = result.items.filter((_, index) => !hydrated[index]).map(item => item.handle);
          if (this.excluded.length + next.length > 6) throw new Error('products_changed');
          this.excluded.push(...next);
          return this.recommend();
        }
        this.items = hydrated;
        this.renderResult(result);
      } catch (error) {
        if (version !== this.requestVersion) return;
        const keys = { setup_required: 'error_setup', rate_limited: 'error_rate', capacity_reached: 'error_rate', no_matching_products: 'error_no_match', session_expired: 'error_session', products_changed: 'products_changed' };
        this.querySelector('[data-error-text]').textContent = this.copy[keys[error.message] || 'error_generic'];
        this.setPane('error');
        if (error.message === 'session_expired') { this.token = ''; this.sessionPromise = null; }
      }
    }
    async hydrate(item) {
      const response = await fetch(`${this.root}products/${item.handle}.js`, { headers: { Accept: 'application/json' }, signal: AbortSignal.timeout(12000) });
      if (!response.ok) return null;
      const product = await response.json();
      const variants = product.variants.filter(variant => variant.available && (!this.state.budget || variant.price <= this.state.budget * 100));
      if (!variants.length) return null;
      const variant = variants[0];
      return { ...item, product, variant, priceCents: variant.price, multiple: product.variants.length > 1, image: variant.featured_image?.src || product.featured_image || product.images?.[0] || '' };
    }
    element(tag, className, text) {
      const element = document.createElement(tag);
      if (className) element.className = className;
      if (text !== undefined) element.textContent = text;
      return element;
    }
    treatmentKey() {
      const goals = this.state.goals || [];
      const preferences = this.state.preferences || [];
      const primary = goals[0];
      const gentle = preferences.includes('gentle') || goals.includes('calming');
      const experienced = preferences.includes('experienced');
      if (!primary || primary === 'unsure' || primary === 'sun') return 'consult';
      if (gentle && goals.includes('blemishes')) return 'plasma';
      if (gentle || primary === 'calming') return 'oxymizer';
      if (primary === 'antiaging') return experienced && goals.includes('texture') ? 'needling' : 'radio';
      if (primary === 'texture') return 'microderm';
      if (primary === 'pigmentation') return experienced ? 'acid' : 'hydrafacial';
      if (primary === 'hydration') return 'oxymizer';
      return 'hydrafacial';
    }
    studioBookingUrl(value) {
      try {
        const url = new URL(value);
        if (url.protocol !== 'https:' || !['www.studiobookr.com', 'studiobookr.com'].includes(url.hostname) || url.username || url.password) return null;
        return url.href;
      } catch { return null; }
    }
    renderStudioBookings(treatment) {
      const booking = this.querySelector('[data-studio-booking]');
      const grid = this.querySelector('[data-studio-grid]');
      grid.replaceChildren(); booking.hidden = true;
      const seen = new Set();
      for (const studio of Array.isArray(treatment.studios) ? treatment.studios : []) {
        const url = this.studioBookingUrl(studio.url);
        if (!url || seen.has(url) || typeof studio.address !== 'string' || !studio.address.trim() || typeof studio.district !== 'string') continue;
        seen.add(url);
        const card = this.element('div', 'fska__studio-card');
        const address = this.element('div', 'fska__studio-address');
        address.append(this.element('span', 'fska__studio-district', studio.district), this.element('strong', 'fska__studio-street', studio.address));
        const link = this.element('a', 'fska__button fska__studio-book-button', this.copy.studio_booking_action);
        link.href = url;
        link.setAttribute('data-fs-booking-url', url);
        link.setAttribute('aria-label', this.copy.studio_booking_accessible.replace('__TREATMENT__', treatment.title).replace('__STUDIO__', studio.address + ', ' + studio.district));
        card.append(address, link); grid.append(card);
      }
      if (!seen.size) return;
      grid.setAttribute('data-count', String(seen.size));
      booking.hidden = false;
    }
    renderTreatment() {
      const panel = this.querySelector('[data-treatment]');
      const treatment = this.treatments?.[this.treatmentKey()] || this.treatments?.consult;
      panel.hidden = true;
      if (!treatment?.url) return;
      const url = new URL(treatment.url, location.origin);
      if (url.origin !== location.origin || !url.pathname.startsWith(this.root + 'pages/')) return;
      this.querySelector('[data-treatment-title]').textContent = treatment.title;
      this.querySelector('[data-treatment-text]').textContent = treatment.text;
      this.querySelector('[data-treatment-link]').href = url.pathname + url.search;
      this.querySelector('[data-treatment-focus]').replaceChildren(...treatment.focus.map(text => this.element('span', '', text)));
      this.renderStudioBookings(treatment);
      panel.hidden = false;
    }
    customerText(value) {
      return String(value || '').replace(/\banti[\s\u2010-\u2015-]*aging\b/gi, 'Anti-Aging').replace(/\b(?:Du|Dich|Dir|Dein(?:e|en|er|em|es)?)\b/g, (word, offset, text) => {
        const before = text.slice(0, offset).trimEnd();
        return !before || /[.!?]$/.test(before) ? word : word.toLowerCase();
      });
    }
    renderResult(result) {
      const products = this.querySelector('[data-products]');
      products.replaceChildren();
      products.setAttribute('data-count', String(this.items.length));
      this.querySelector('[data-result]').setAttribute('data-product-count', String(this.items.length));
      for (const item of this.items) {
        const url = `${this.root}products/${item.handle}?variant=${encodeURIComponent(item.variant.id)}`;
        const card = this.element('article', 'fska__product');
        const imageLink = this.element('a', 'fska__product-image'); imageLink.href = url; imageLink.setAttribute('aria-label', item.title);
        if (item.image) {
          const src = new URL(item.image, location.origin);
          if (src.protocol === 'https:' || src.origin === location.origin) {
            src.searchParams.set('width', '650');
            const image = this.element('img'); image.src = src.href; image.alt = item.title; image.width = 650; image.height = 520; image.loading = 'lazy'; image.decoding = 'async'; imageLink.append(image);
          }
        }
        imageLink.append(this.element('span', 'fska__product-role', this.copy[`role_${item.role}`] || ''));
        const body = this.element('div', 'fska__product-body');
        body.append(this.element('p', 'fska__product-brand', item.brand));
        const heading = this.element('h3'); const titleLink = this.element('a', '', item.title); titleLink.href = url; heading.append(titleLink); body.append(heading);
        body.append(this.element('p', 'fska__reason', this.customerText(item.reason)));
        const bottom = this.element('div', 'fska__product-bottom');
        bottom.append(this.element('p', 'fska__price', this.money.format(item.priceCents / 100)));
        const unit = item.variant.unit_price_measurement;
        const unitText = item.variant.unit_price && unit ? `${this.money.format(item.variant.unit_price / 100)} / ${unit.reference_value === 1 ? '' : unit.reference_value} ${unit.reference_unit}` : '';
        bottom.append(this.element('p', 'fska__unit', unitText));
        const link = this.element('a', 'fska__button', this.copy.product_link); link.href = url; bottom.append(link);
        if (item.multiple) bottom.append(this.element('p', 'fska__small', this.copy.variant_required));
        body.append(bottom); card.append(imageLink, body); products.append(card);
      }
      this.querySelector('[data-summary]').textContent = this.customerText(result.summary);
      this.querySelector('[data-incomplete]').hidden = !result.incomplete;
      this.querySelector('[data-total]').textContent = this.money.format(this.items.reduce((sum, item) => sum + item.priceCents, 0) / 100);
      this.querySelector('[data-result-profile]').replaceChildren(...this.labels().map(label => this.element('span', '', label)));
      this.querySelector('[data-add-routine]').hidden = this.items.some(item => item.multiple);
      this.cartRefreshNeeded = false;
      this.cartNeedsReview = false;
      this.querySelector('[data-add-routine]').textContent = this.copy.add_routine;
      this.querySelector('[data-cart-message]').textContent = '';
      this.renderTreatment();
      this.setPane('result');
      const heading = this.querySelector('[data-result] h2'); heading.focus({ preventScroll: true });
      this.querySelector('[data-result]').scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }
    async readCartQuantities() {
      const response = await fetch(`${this.root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store', signal: AbortSignal.timeout(12000) });
      if (!response.ok) throw new Error('cart_check_error');
      const cart = await response.json();
      if (!Array.isArray(cart.items)) throw new Error('cart_check_error');
      const quantities = new Map();
      for (const item of cart.items) {
        const id = String(item.variant_id ?? item.id ?? '');
        if (!/^\d+$/.test(id) || !Number.isSafeInteger(item.quantity) || item.quantity < 0) throw new Error('cart_check_error');
        quantities.set(id, (quantities.get(id) || 0) + item.quantity);
      }
      return quantities;
    }
    async cartIncludesAddedItems(before, items) {
      try {
        const after = await this.readCartQuantities();
        return items.every(item => (after.get(String(item.id)) || 0) >= (before.get(String(item.id)) || 0) + item.quantity);
      } catch { return false; }
    }
    async addRoutine() {
      if (this.cartBusy) return;
      if (this.cartNeedsReview) { location.assign(`${this.root}cart`); return; }
      if (this.cartRefreshNeeded) { this.cartRefreshNeeded = false; return this.recommend(); }
      const button = this.querySelector('[data-add-routine]');
      const message = this.querySelector('[data-cart-message]');
      this.cartBusy = true;
      button.disabled = true; button.textContent = this.copy.adding; message.textContent = '';
      let before, items, mutationStarted = false;
      try {
        const fresh = await Promise.all(this.items.map(item => this.hydrate(item)));
        if (fresh.some((item, index) => !item || item.multiple || item.variant.id !== this.items[index].variant.id || item.priceCents !== this.items[index].priceCents)) throw new Error('products_changed');
        before = await this.readCartQuantities();
        items = fresh.map(item => ({ id: item.variant.id, quantity: 1 }));
        mutationStarted = true;
        const response = await fetch(`${this.root}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }), signal: AbortSignal.timeout(15000) });
        if (!response.ok) throw new Error('cart_error');
        message.textContent = this.copy.added;
        this.cartNeedsReview = true;
        location.assign(`${this.root}cart`);
      } catch (error) {
        if (mutationStarted) {
          this.cartNeedsReview = true;
          if (await this.cartIncludesAddedItems(before, items)) {
            message.textContent = this.copy.added;
            location.assign(`${this.root}cart`);
          } else message.textContent = this.copy.cart_uncertain;
        } else if (error.message === 'products_changed') {
          this.cartRefreshNeeded = true;
          message.textContent = this.copy.products_changed;
        } else message.textContent = this.copy.cart_check_error;
      } finally {
        this.cartBusy = false;
        button.disabled = false;
        button.textContent = this.cartNeedsReview ? this.copy.review_cart : this.cartRefreshNeeded ? this.copy.refresh_selection : this.copy.add_routine;
      }
    }
  }
  customElements.define('fsk-skin-advisor', SkinAdvisor);
})();

